/*
 * wind-particles.js — animated wind streaks (Wind / Wind Gusts) and falling rain and snow
 * (Radar, Precipitation 1 hr / 24 hr, Snowfall 24 hr) over the map. ONE shared copy: iOS,
 * Android and web load this file byte for byte. iOS holds the canonical copy; change it
 * there and re-copy everywhere.
 *
 * API (window.BMWind):
 *   BMWind.install(map)                         idempotent; adds the overlay canvas and camera hooks
 *   BMWind.setField({url, kind, windUrl, hours, lo, hi})
 *                                               kind 'wind' (default): a BMUVGRD1 .uv25 to animate.
 *                                               kind 'precip': a BMPXGRD1 .px25, plus optionally the
 *                                               nearest wind .uv25 for drift, and the layer's rain
 *                                               scale: amount = rate x hours on log lo..hi inches
 *                                               (precip_24h 24/0.1/2; precip_1h, radar 1/0.01/1).
 *                                               null / no url hides.
 *   BMWind.setEnabled(bool)                     false while the time bar plays, in Low Power, etc.
 *   BMWind.clear()                              drop particles and fields
 *
 * Map surface used (web runs this file on Google Maps through an adapter supplying exactly
 * these): getCanvas, getCanvasContainer, unproject, on/off (movestart, moveend, resize),
 * getZoom, and map.transform when present (feature-detected). Zooms are MapLibre zooms.
 *
 * WIND (approved 2026-10-19): 1 particle per 400 pt², cap 1500, life 1.5–3.5 s; on-screen speed
 * 1.5 pt/s per mph (cap 60) at every zoom; ~1 s trail at alpha 0.85 x 0.92^n, 1.5 pt, white.
 *
 * RAIN / SNOW (approved 2026-10-19, docs/rain-snow-particles-design.md in gis-pipeline):
 * - Visual rate v = rate x (1 + 9 x snow); density d = clamp(log10(v/0.002)/log10(250), 0, 1),
 *   at least 0.12 wherever it is wet. Count = mean(d over the view) x min(cap, area / 150 pt²),
 *   once per camera settle. Caps: rain 2,500, snow 1,500. Spawn by rejection on d.
 * - Each particle is snow with probability = the snow share. Snow: radius 1.5–2.5 pt, white at 90%,
 *   30 pt/s down-screen, wobble ±5 pt at 0.5–1.0 Hz, life 2.5–4.5 s. (Snow count: mean(d x snow
 *   share) x min(2,500, area / 150 pt²), at most 1,500.)
 * - RAIN (Matt's retune, 2026-10-19): intensity i = clamp(log(A/lo)/log(hi/lo)) on the layer's
 *   amount A = rate x hours, at least 0.12 when wet. Count = mean(i x rain share) x min(4,000,
 *   area / 120 pt²). Each drop interpolates by its spawn i: length 6→20 pt, 300→700 pt/s,
 *   alpha 35→75%, width 1→1.5 pt; #D6ECFF, life 0.25–0.5 s.
 * - Drift from the wind field: rain 0.6 pt/s per mph, snow 1.5 pt/s per mph (cap 40 pt/s).
 * - Off below MapLibre zoom 5.5, full by 6.5.
 *
 * Both: 30 fps, fade out (150 ms) when the camera starts moving, re-seed when it settles.
 * Drawing is a 2D canvas over the map, not a GL custom layer: WebGL line widths are clamped
 * to 1 device px on most Android GPUs, and a custom layer would repaint the whole map at
 * 30 fps. Canvas resolution is capped at 2x.
 *
 * Field formats (gzip as application/gzip, no Content-Encoding; little-endian): an 8-byte
 * magic ("BMUVGRD1" wind, "BMPXGRD1" precipitation), f64 north, f64 west, f64 cell, i32 rows,
 * i32 cols, f32 scale, f32 offset, i16 nodata, then two rows*cols i16 planes, north row
 * first; value = raw * scale + offset. Wind: u (toward east), v (toward north), mph.
 * Precipitation: rate (in/h, liquid-equivalent), snow share (0..1). Cell (r, c) spans
 * lon west + c*cell .. + cell.
 */
(function () {
  'use strict';
  if (window.BMWind) return;

  // Wind
  var DENSITY_PT2 = 400, MAX_PARTICLES = 1500;
  var LIFE_MIN = 1.5, LIFE_MAX = 3.5;
  var PT_PER_SEC_PER_MPH = 1.5, MAX_PT_PER_SEC = 60;
  var TRAIL_ALPHA = 0.85, TRAIL_FADE = 0.92, LINE_WIDTH = 1.5;
  var TRAIL_POINTS = 27;   // 0.85 * 0.92^26 < 0.1: older segments are invisible anyway
  var ALPHA_BUCKETS = 13;  // strokes per frame; segments share the alpha of their bucket (7 looked dashed)

  // Rain and snow
  var PRECIP_AREA_PT2 = 150, SNOW_AREA_CAP = 2500, SNOW_CAP = 1500;
  var V_FLOOR = 0.002, V_FULL = 0.5, D_WET_MIN = 0.12;
  // Rain (Matt's 2026-10-19 retune): intensity i from the period's amount on the layer's own scale
  // (setField hours/lo/hi); each drop's length, speed, opacity and width follow its i.
  var RAIN_AREA_PT2 = 120, RAIN_CAP = 4000, RAIN_BUCKETS = 6;
  var RAIN_LENGTH_MIN = 6, RAIN_LENGTH_MAX = 20, RAIN_SPEED_MIN = 300, RAIN_SPEED_MAX = 700;
  var RAIN_ALPHA_MIN = 0.35, RAIN_ALPHA_MAX = 0.75, RAIN_WIDTH_MIN = 1, RAIN_WIDTH_MAX = 1.5;
  var RAIN_LIFE_MIN = 0.25, RAIN_LIFE_MAX = 0.5;
  var rainScale = { hours: 1, lo: 0.01, hi: 1 };
  var SNOW_R_MIN = 1.5, SNOW_R_MAX = 2.5, SNOW_SPEED = 30, SNOW_WOBBLE = 5;
  var SNOW_HZ_MIN = 0.5, SNOW_HZ_MAX = 1.0, SNOW_LIFE_MIN = 2.5, SNOW_LIFE_MAX = 4.5;
  var SNOW_COLOR = 'rgba(255,255,255,0.9)';
  var RAIN_DRIFT_PER_MPH = 0.6, SNOW_DRIFT_PER_MPH = 1.5, SNOW_DRIFT_MAX = 40;
  var ZOOM_OFF = 5.5, ZOOM_FULL = 6.5;

  // Shared
  var FPS = 30, DT = 1 / FPS;
  var GRID_X = 32, GRID_Y = 64;
  var CACHE_SIZE = 3, FADE_MS = 150, SPAWN_TRIES = 30, MAX_DPR = 2;

  var map = null, canvas = null, ctx = null;
  var width = 0, height = 0, dpr = 1;
  var kind = 'wind';
  var field = null, fieldUrl = null, loadToken = 0;          // the animated field
  var windField = null, windUrl = null, windToken = 0;       // precipitation drift
  var cache = [];
  var enabled = true, moving = false, noData = false;
  var grid = null;
  var particles = [];
  var precipTarget = 0, snowCount = 0, zoomAlpha = 1, clock = 0;
  var rafId = 0, lastFrame = 0, visibilityHooked = false;

  // ---- Fields ---------------------------------------------------------------------

  function parseField(buffer) {
    if (buffer.byteLength < 50) return null;
    var dv = new DataView(buffer);
    var magic = '';
    for (var i = 0; i < 8; i++) magic += String.fromCharCode(dv.getUint8(i));
    if (magic !== 'BMUVGRD1' && magic !== 'BMPXGRD1') return null;
    var f = {
      magic: magic,
      north: dv.getFloat64(8, true), west: dv.getFloat64(16, true), cell: dv.getFloat64(24, true),
      rows: dv.getInt32(32, true), cols: dv.getInt32(36, true),
      scale: dv.getFloat32(40, true), offset: dv.getFloat32(44, true), nodata: dv.getInt16(48, true)
    };
    var n = f.rows * f.cols;
    if (n <= 0 || buffer.byteLength < 50 + n * 4) return null;
    f.a = new Int16Array(buffer, 50, n);           // u, or rate
    f.b = new Int16Array(buffer, 50 + n * 2, n);   // v, or snow share
    return f;
  }

  function cached(url) {
    for (var i = 0; i < cache.length; i++) if (cache[i].url === url) return cache[i].field;
    return null;
  }

  function remember(url, f) {
    cache = cache.filter(function (e) { return e.url !== url; });
    cache.unshift({ url: url, field: f });
    if (cache.length > CACHE_SIZE) cache.length = CACHE_SIZE;
  }

  // Fetches, gunzips and parses `url`; calls done(field) or done(null) on a 404 or failure.
  function fetchField(url, done) {
    var hit = cached(url);
    if (hit) { done(hit); return; }
    fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      // Sent as application/gzip with no Content-Encoding, so the browser leaves it to us.
      var stream = response.body.pipeThrough(new DecompressionStream('gzip'));
      return new Response(stream).arrayBuffer();
    }).then(function (buffer) {
      var f = parseField(buffer);
      if (!f) throw new Error('not a BMUVGRD1 / BMPXGRD1 field');
      remember(url, f);
      done(f);
    }).catch(function () { done(null); });
  }

  // Both planes at a lon/lat, bilinear, as [a, b]; null over nodata or off the grid.
  function sample(f, lon, lat) {
    var fx = (lon - f.west) / f.cell - 0.5;
    var fy = (f.north - lat) / f.cell - 0.5;
    var c0 = Math.floor(fx), r0 = Math.floor(fy);
    if (c0 < 0 || r0 < 0 || c0 + 1 >= f.cols || r0 + 1 >= f.rows) return null;
    var a = fx - c0, b = fy - r0;
    var i00 = r0 * f.cols + c0, i01 = i00 + 1, i10 = i00 + f.cols, i11 = i10 + 1;
    var nd = f.nodata, A = f.a, B = f.b;
    if (A[i00] === nd || A[i01] === nd || A[i10] === nd || A[i11] === nd) return null;
    var w00 = (1 - a) * (1 - b), w01 = a * (1 - b), w10 = (1 - a) * b, w11 = a * b;
    var aa = A[i00] * w00 + A[i01] * w01 + A[i10] * w10 + A[i11] * w11;
    var bb = B[i00] * w00 + B[i01] * w01 + B[i10] * w10 + B[i11] * w11;
    return [aa * f.scale + f.offset, bb * f.scale + f.offset];
  }

  // ---- Screen <-> geo -------------------------------------------------------------

  // lon/lat at a 32x64 lattice of screen points, built once per camera settle. Bilinear
  // lookups between them handle bearing and pitch without per-particle unproject calls.
  //
  // Not map.unproject(): with terrain on it ray-casts every point against the elevation
  // mesh. Measured on a Galaxy A36 (terrain, pitch 49°): 2,048 unprojects = 5.6 s, on every
  // camera settle, which froze the phone. The transform's own projection onto the sea-level
  // plane does the same lattice in ~5 ms. Particles are a screen overlay sampled from a 0.25°
  // field, so the small shift between the plane and the terrain surface doesn't show. With a
  // bundled SDK there may be no global Point class; the flat-plane path only reads x and y.
  function unprojectFlat(x, y) {
    var tr = map.transform;
    if (tr && typeof tr.screenPointToLocation === 'function') {
      var P = (window.maptilersdk && window.maptilersdk.Point) || (window.maplibregl && window.maplibregl.Point);
      var ll = tr.screenPointToLocation(P ? new P(x, y) : { x: x, y: y });
      if (ll) return ll;
    }
    return map.unproject([x, y]);
  }

  function buildGrid() {
    var lon = new Float64Array(GRID_X * GRID_Y), lat = new Float64Array(GRID_X * GRID_Y);
    var ok = new Uint8Array(GRID_X * GRID_Y);
    for (var j = 0; j < GRID_Y; j++) {
      for (var i = 0; i < GRID_X; i++) {
        var k = j * GRID_X + i;
        var ll = unprojectFlat(i * width / (GRID_X - 1), j * height / (GRID_Y - 1));
        lon[k] = ll.lng; lat[k] = ll.lat;
        ok[k] = isFinite(ll.lng) && isFinite(ll.lat) && Math.abs(ll.lat) < 85 ? 1 : 0;
      }
    }
    grid = { lon: lon, lat: lat, ok: ok, dx: width / (GRID_X - 1), dy: height / (GRID_Y - 1) };
  }

  // The geo point under screen (x, y) and the local Jacobian of (lon, lat) w.r.t. (x, y).
  function locate(x, y) {
    if (!grid) return null;
    var gx = x / grid.dx, gy = y / grid.dy;
    var i = Math.min(GRID_X - 2, Math.max(0, Math.floor(gx)));
    var j = Math.min(GRID_Y - 2, Math.max(0, Math.floor(gy)));
    var a = gx - i, b = gy - j;
    var k00 = j * GRID_X + i, k10 = k00 + 1, k01 = k00 + GRID_X, k11 = k01 + 1;
    if (!(grid.ok[k00] && grid.ok[k10] && grid.ok[k01] && grid.ok[k11])) return null;
    var L = grid.lon, T = grid.lat;
    var jxl = ((L[k10] - L[k00]) * (1 - b) + (L[k11] - L[k01]) * b) / grid.dx;
    var jyl = ((L[k01] - L[k00]) * (1 - a) + (L[k11] - L[k10]) * a) / grid.dy;
    var jxt = ((T[k10] - T[k00]) * (1 - b) + (T[k11] - T[k01]) * b) / grid.dx;
    var jyt = ((T[k01] - T[k00]) * (1 - a) + (T[k11] - T[k10]) * a) / grid.dy;
    return {
      lon: L[k00] * (1 - a) * (1 - b) + L[k10] * a * (1 - b) + L[k01] * (1 - a) * b + L[k11] * a * b,
      lat: T[k00] * (1 - a) * (1 - b) + T[k10] * a * (1 - b) + T[k01] * (1 - a) * b + T[k11] * a * b,
      jxl: jxl, jyl: jyl, jxt: jxt, jyt: jyt, det: jxl * jyt - jyl * jxt
    };
  }

  // The wind at a located point as [unit screen x, unit screen y, mph], or null.
  function windOnScreen(f, loc) {
    if (!f || !loc || !loc.det) return null;
    var wind = sample(f, loc.lon, loc.lat);
    if (!wind) return null;
    var speed = Math.sqrt(wind[0] * wind[0] + wind[1] * wind[1]);
    if (speed < 0.05) return [0, 0, 0];
    // Wind direction in degrees per unit time, then mapped back onto the screen.
    var dLon = wind[0] / Math.cos(loc.lat * Math.PI / 180), dLat = wind[1];
    var sx = (loc.jyt * dLon - loc.jyl * dLat) / loc.det;
    var sy = (-loc.jxt * dLon + loc.jxl * dLat) / loc.det;
    var norm = Math.sqrt(sx * sx + sy * sy);
    if (!norm) return [0, 0, 0];
    return [sx / norm, sy / norm, speed];
  }

  // ---- Wind streaks ---------------------------------------------------------------

  // Wind as an on-screen velocity in pt/s at screen point (x, y), or null.
  function velocityAt(x, y) {
    var w = windOnScreen(field, locate(x, y));
    if (!w) return null;
    var ptPerSec = Math.min(w[2] * PT_PER_SEC_PER_MPH, MAX_PT_PER_SEC);
    return [w[0] * ptPerSec, w[1] * ptPerSec];
  }

  function newStreak() {
    return { x: 0, y: 0, age: 0, life: 0, trail: new Float32Array(TRAIL_POINTS * 2), count: 0 };
  }

  function spawnStreak(p) {
    for (var t = 0; t < SPAWN_TRIES; t++) {
      var x = Math.random() * width, y = Math.random() * height;
      if (velocityAt(x, y)) {
        p.x = x; p.y = y; p.age = 0;
        p.life = LIFE_MIN + Math.random() * (LIFE_MAX - LIFE_MIN);
        p.trail[0] = x; p.trail[1] = y; p.count = 1;
        return true;
      }
    }
    p.count = 0;
    return false;
  }

  function seedStreaks() {
    var want = Math.min(MAX_PARTICLES, Math.floor(width * height / DENSITY_PT2));
    particles = [];
    var alive = 0;
    for (var i = 0; i < want; i++) {
      var p = newStreak();
      if (spawnStreak(p)) alive++;
      // Stagger ages so the first wave doesn't all respawn on the same frame.
      p.age = Math.random() * p.life;
      particles.push(p);
    }
    // The whole view is ocean / Canada / sky: nothing to draw until the camera moves.
    noData = alive === 0;
  }

  function stepStreaks() {
    var alive = 0;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.age += DT;
      var vel = p.count ? velocityAt(p.x, p.y) : null;
      if (p.age > p.life || !vel) {
        if (spawnStreak(p)) alive++;
        continue;
      }
      var nx = p.x + vel[0] * DT, ny = p.y + vel[1] * DT;
      if (nx < 0 || ny < 0 || nx > width || ny > height) {
        if (spawnStreak(p)) alive++;
        continue;
      }
      p.x = nx; p.y = ny;
      // Newest point first; drop the oldest.
      p.trail.copyWithin(2, 0, (TRAIL_POINTS - 1) * 2);
      p.trail[0] = nx; p.trail[1] = ny;
      if (p.count < TRAIL_POINTS) p.count++;
      alive++;
    }
    if (alive === 0) noData = true;
  }

  function drawStreaks() {
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    var perBucket = Math.ceil((TRAIL_POINTS - 1) / ALPHA_BUCKETS);
    for (var bucket = 0; bucket < ALPHA_BUCKETS; bucket++) {
      var first = bucket * perBucket, last = Math.min(TRAIL_POINTS - 1, first + perBucket);
      var alpha = TRAIL_ALPHA * Math.pow(TRAIL_FADE, (first + last) / 2);
      ctx.strokeStyle = 'rgba(255,255,255,' + alpha.toFixed(3) + ')';
      ctx.beginPath();
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i], t = p.trail, end = Math.min(last, p.count - 1);
        if (end <= first) continue;
        ctx.moveTo(t[first * 2], t[first * 2 + 1]);
        for (var s = first + 1; s <= end; s++) ctx.lineTo(t[s * 2], t[s * 2 + 1]);
      }
      ctx.stroke();
    }
  }

  // ---- Rain and snow --------------------------------------------------------------

  // Density and snow share at a located point, or null over nodata / off the grid.
  function precipAt(loc) {
    if (!loc || !field) return null;
    var s = sample(field, loc.lon, loc.lat);
    if (!s) return null;
    var rate = s[0], snow = Math.min(1, Math.max(0, s[1]));
    if (!(rate > 0)) return { d: 0, i: 0, snow: snow };
    // Snow is scored on depth at 10:1; liquid-equivalent alone would hide light snow.
    var v = rate * (1 + 9 * snow);
    var d = Math.log(v / V_FLOOR) / Math.log(V_FULL / V_FLOOR);
    d = Math.max(D_WET_MIN, Math.min(1, d));
    // Rain: the period's amount on this layer's own scale.
    var amount = rate * rainScale.hours;
    var ri = Math.log(amount / rainScale.lo) / Math.log(rainScale.hi / rainScale.lo);
    ri = Math.max(D_WET_MIN, Math.min(1, ri));
    return { d: d, i: ri, snow: snow };
  }

  function lerp(a, b, t) { return a + (b - a) * Math.max(0, Math.min(1, t)); }

  function rand(min, max) { return min + Math.random() * (max - min); }

  // A slot's kind never changes: seeding fills the snow slots first, then rain. A spot is accepted
  // with probability d x snow share (snow) or i x rain share (rain). Re-rolling the kind on every
  // respawn would drift the mix towards snow, because a raindrop respawns ~10x as often as a flake.
  // A raindrop keeps the intensity of its spawn point.
  function spawnDrop(p) {
    for (var t = 0; t < SPAWN_TRIES; t++) {
      var x = Math.random() * width, y = Math.random() * height;
      var pa = precipAt(locate(x, y));
      if (!pa || pa.d <= 0) continue;
      var accept = p.snow ? pa.d * pa.snow : pa.i * (1 - pa.snow);
      if (Math.random() > accept) continue;
      p.x = x; p.y = y; p.age = 0; p.alive = true;
      if (p.snow) {
        p.vx = 0; p.vy = SNOW_SPEED;
        p.life = rand(SNOW_LIFE_MIN, SNOW_LIFE_MAX);
        p.r = rand(SNOW_R_MIN, SNOW_R_MAX);
        p.hz = rand(SNOW_HZ_MIN, SNOW_HZ_MAX);
        p.phase = Math.random() * Math.PI * 2;
      } else {
        p.i = pa.i;
        p.speed = lerp(RAIN_SPEED_MIN, RAIN_SPEED_MAX, pa.i);
        p.vx = 0; p.vy = p.speed;
        p.life = rand(RAIN_LIFE_MIN, RAIN_LIFE_MAX);
      }
      return true;
    }
    // No spot this frame (the slot's kind may be absent from the view); try again next frame.
    p.alive = false;
    return false;
  }

  function seedDrops() {
    particles = [];
    zoomAlpha = Math.max(0, Math.min(1, (map.getZoom() - ZOOM_OFF) / (ZOOM_FULL - ZOOM_OFF)));
    var snowSum = 0, rainSum = 0, n = 0;
    if (grid && field && zoomAlpha > 0) {
      for (var k = 0; k < grid.ok.length; k++) {
        if (!grid.ok[k]) continue;
        n++;
        var s = precipAt({ lon: grid.lon[k], lat: grid.lat[k] });
        if (!s || s.d <= 0) continue;
        snowSum += s.d * s.snow;
        rainSum += s.i * (1 - s.snow);
      }
    }
    var area = width * height;
    var snowTarget = n ? Math.min(SNOW_CAP, Math.round(snowSum / n * Math.min(SNOW_AREA_CAP, area / PRECIP_AREA_PT2))) : 0;
    var rainTarget = n ? Math.round(rainSum / n * Math.min(RAIN_CAP, area / RAIN_AREA_PT2)) : 0;
    precipTarget = snowTarget + rainTarget;
    snowCount = snowTarget;
    var alive = 0;
    for (var i = 0; i < precipTarget; i++) {
      var p = { alive: false, snow: i < snowTarget, life: 0 };
      if (spawnDrop(p)) alive++;
      p.age = Math.random() * p.life;
      particles.push(p);
    }
    // Dry view, or zoomed out past the ramp: nothing to draw until the camera moves.
    noData = alive === 0;
  }

  function stepDrops() {
    clock += DT;
    var alive = 0;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.age += DT;
      if (!p.alive || p.age > p.life) {
        if (spawnDrop(p)) alive++;
        continue;
      }
      var vx = 0, vy = p.snow ? SNOW_SPEED : p.speed;
      var w = windOnScreen(windField, windField ? locate(p.x, p.y) : null);
      if (w) {
        var drift = p.snow ? Math.min(w[2] * SNOW_DRIFT_PER_MPH, SNOW_DRIFT_MAX) : w[2] * RAIN_DRIFT_PER_MPH;
        vx += w[0] * drift; vy += w[1] * drift;
      }
      if (p.snow) vx += SNOW_WOBBLE * 2 * Math.PI * p.hz * Math.cos(2 * Math.PI * p.hz * clock + p.phase);
      p.vx = vx; p.vy = vy;
      p.x += vx * DT; p.y += vy * DT;
      if (p.x < 0 || p.y < 0 || p.x > width || p.y > height) {
        if (spawnDrop(p)) alive++;
        continue;
      }
      alive++;
    }
    if (alive === 0 && precipTarget === 0) noData = true;
  }

  function drawDrops() {
    ctx.globalAlpha = zoomAlpha;
    ctx.lineCap = 'butt';
    // Rain in intensity buckets: heavier drops are longer, more opaque and wider.
    for (var b = 0; b < RAIN_BUCKETS; b++) {
      var t = (b + 0.5) / RAIN_BUCKETS;
      ctx.lineWidth = lerp(RAIN_WIDTH_MIN, RAIN_WIDTH_MAX, t);
      ctx.strokeStyle = 'rgba(214,236,255,' + lerp(RAIN_ALPHA_MIN, RAIN_ALPHA_MAX, t).toFixed(3) + ')';
      ctx.beginPath();
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        if (!p.alive || p.snow) continue;
        if (Math.min(RAIN_BUCKETS - 1, Math.floor(p.i * RAIN_BUCKETS)) !== b) continue;
        var speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy) || 1;
        var len = lerp(RAIN_LENGTH_MIN, RAIN_LENGTH_MAX, p.i);
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - p.vx / speed * len, p.y - p.vy / speed * len);
      }
      ctx.stroke();
    }
    ctx.fillStyle = SNOW_COLOR;
    ctx.beginPath();
    for (var j = 0; j < particles.length; j++) {
      var q = particles[j];
      if (!q.alive || !q.snow) continue;
      ctx.moveTo(q.x + q.r, q.y);
      ctx.arc(q.x, q.y, q.r, 0, Math.PI * 2);
    }
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ---- Loop and lifecycle -------------------------------------------------------------

  function shouldRun() {
    return !!(map && canvas && enabled && field && !moving && !noData && document.visibilityState !== 'hidden');
  }

  function frame(now) {
    rafId = 0;
    if (!shouldRun()) return;
    if (now - lastFrame >= 1000 / FPS - 2) {
      lastFrame = now;
      ctx.clearRect(0, 0, width, height);
      if (kind === 'precip') { stepDrops(); drawDrops(); } else { stepStreaks(); drawStreaks(); }
    }
    rafId = requestAnimationFrame(frame);
  }

  function update() {
    var run = shouldRun();
    if (canvas) canvas.style.opacity = run ? '1' : '0';
    if (run && !rafId) rafId = requestAnimationFrame(frame);
    if (!run && rafId) { cancelAnimationFrame(rafId); rafId = 0; }
  }

  function resize() {
    var mapCanvas = map.getCanvas();
    dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    width = mapCanvas.clientWidth; height = mapCanvas.clientHeight;
    canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
    canvas.style.width = width + 'px'; canvas.style.height = height + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function restart() {
    if (!map || !canvas) return;
    resize();
    buildGrid();
    noData = false;
    particles = [];
    if (field) { if (kind === 'precip') seedDrops(); else seedStreaks(); }
    if (ctx) ctx.clearRect(0, 0, width, height);
    update();
  }

  function useField(f) {
    var hadField = !!field;
    field = f;
    // Particles keep their places across a swap of the same kind; seed only from nothing.
    // A new precipitation field re-counts, since its density sets how many there are.
    if (f && (!hadField || particles.length === 0 || kind === 'precip')) restart();
    else update();
  }

  function onMoveStart() {
    moving = true;
    update();
  }

  function onMoveEnd() {
    moving = false;
    // Let the 150 ms fade-out finish before the old particles are dropped.
    setTimeout(function () { if (!moving) restart(); }, FADE_MS);
  }

  window.BMWind = {
    install: function (m) {
      if (map === m && canvas && canvas.parentNode) return;
      // Unhook unconditionally: a style swap can detach the canvas while `map` stays the
      // same, and re-installing would otherwise double every camera listener.
      if (map) {
        map.off('movestart', onMoveStart); map.off('moveend', onMoveEnd); map.off('resize', onMoveEnd);
      }
      map = m;
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'bm-wind-particles';
        canvas.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;opacity:0;transition:opacity ' + FADE_MS + 'ms linear;';
        ctx = canvas.getContext('2d');
      }
      map.getCanvasContainer().appendChild(canvas);
      map.on('movestart', onMoveStart);
      map.on('moveend', onMoveEnd);
      map.on('resize', onMoveEnd);
      if (!visibilityHooked) {
        document.addEventListener('visibilitychange', update);
        visibilityHooked = true;
      }
      restart();
    },
    setField: function (opts) {
      var url = opts && opts.url ? opts.url : null;
      var newKind = opts && opts.kind === 'precip' ? 'precip' : 'wind';
      var newWind = newKind === 'precip' && opts.windUrl ? opts.windUrl : null;
      // Rain intensity scale per layer (precip_24h: 24, 0.1, 2; precip_1h and radar: 1, 0.01, 1).
      if (newKind === 'precip' && opts.hours > 0 && opts.lo > 0 && opts.hi > opts.lo &&
          (opts.hours !== rainScale.hours || opts.lo !== rainScale.lo || opts.hi !== rainScale.hi)) {
        rainScale = { hours: opts.hours, lo: opts.lo, hi: opts.hi };
        if (url === fieldUrl && field) restart();
      }
      if (newKind !== kind) {
        // A different effect: nothing of the old one carries over.
        kind = newKind;
        field = null; fieldUrl = null; particles = [];
        loadToken++;
      }
      if (newWind !== windUrl) {
        windUrl = newWind;
        var wt = ++windToken;
        if (!newWind) windField = null;
        else fetchField(newWind, function (f) { if (wt === windToken) windField = f; });
      }
      if (url === fieldUrl) return;
      fieldUrl = url;
      var token = ++loadToken;
      if (!url) { useField(null); return; }
      fetchField(url, function (f) { if (token === loadToken) useField(f); });
    },
    setEnabled: function (on) {
      enabled = !!on;
      update();
    },
    clear: function () {
      loadToken++; windToken++;
      field = null; fieldUrl = null; windField = null; windUrl = null; particles = [];
      if (ctx) ctx.clearRect(0, 0, width, height);
      update();
    }
  };
})();
